import { useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, Plus } from "lucide-react";
import { toast } from "sonner";
import { api, apiError, humanize } from "@/lib/api";
import { useWorkspace, useWsQuery } from "@/context/WorkspaceContext";
import { Field, PageHeader, QueryState, StatusBadge } from "@/components/common";
import RecordDialog, { useCrud } from "@/components/RecordDialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const KIND_COLOR = { content: "#2962FF", milestone: "#855BFF", review: "#F4B860", launch: "#4BE3A4" };

function Reschedule({ item, onClose }) {
  const { path, refresh } = useWorkspace();
  const [date, setDate] = useState("");
  const [reason, setReason] = useState("");
  const submit = async () => {
    try { await api.post(path(`/calendar/${item.id}/propose-reschedule`), { new_date: date, reason }); toast.success("Reschedule sent for approval"); refresh(); onClose(); }
    catch (e) { toast.error(apiError(e)); }
  };
  return (
    <Dialog open={!!item} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="reschedule-dialog" className="bg-[#0A1024] border-[#243153] text-[#F7FAFF]">
        <DialogHeader><DialogTitle className="font-display">Propose a schedule change</DialogTitle></DialogHeader>
        <p className="text-sm text-[#AAB6CC]">The date only changes after the request is approved.</p>
        <Field label="New date"><input data-testid="reschedule-date-input" type="date" className="cq-input" value={date} onChange={(e) => setDate(e.target.value)} /></Field>
        <Field label="Reason"><input data-testid="reschedule-reason-input" className="cq-input" value={reason} onChange={(e) => setReason(e.target.value)} /></Field>
        <div className="flex justify-end gap-2"><button onClick={onClose} className="cq-btn cq-btn-ghost">Cancel</button><button data-testid="reschedule-submit" disabled={!date} onClick={submit} className="cq-btn cq-btn-primary">Send for approval</button></div>
      </DialogContent>
    </Dialog>
  );
}

export default function CalendarPage() {
  const q = useWsQuery("/r/calendar_items");
  const camps = useWsQuery("/campaigns");
  const crud = useCrud("calendar_items");
  const [month, setMonth] = useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });
  const [edit, setEdit] = useState(null);
  const [resched, setResched] = useState(null);
  const days = useMemo(() => {
    const start = new Date(month); start.setDate(1 - ((month.getDay() + 6) % 7));
    return Array.from({ length: 42 }, (_, i) => { const d = new Date(start); d.setDate(start.getDate() + i); return d; });
  }, [month]);
  const fields = [
    { k: "title", label: "Title" }, { k: "date", label: "Date", type: "date", half: true }, { k: "time", label: "Time", type: "time", half: true },
    { k: "campaign_id", label: "Campaign", type: "select", allowEmpty: true, half: true, options: (camps.data || []).map((c) => [c.id, c.name]) },
    { k: "channel", label: "Channel", half: true }, { k: "kind", label: "Type", type: "select", half: true, options: ["content", "milestone", "review", "launch"].map((s) => [s, humanize(s)]) },
    { k: "status", label: "Status", type: "select", half: true, options: ["planned", "done"].map((s) => [s, humanize(s)]), hint: "Approval states are set through Approvals" },
    { k: "notes", label: "Notes", type: "textarea", rows: 3 },
  ];
  const today = iso(new Date());

  return (
    <div>
      <PageHeader eyebrow="Plan" title="Calendar" description="Planned activity across campaigns. Nothing here is published automatically."
        actions={<button data-testid="calendar-new-button" onClick={() => setEdit({ date: today, kind: "content", status: "planned" })} className="cq-btn cq-btn-primary"><Plus size={14} /> Add item</button>} />
      <QueryState q={q}>
        {(items) => (<>
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display text-xl text-[#F7FAFF]" data-testid="calendar-month-label">{month.toLocaleDateString(undefined, { month: "long", year: "numeric" })}</h2>
            <div className="flex gap-1">
              <button data-testid="calendar-prev" onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() - 1, 1))} className="cq-btn cq-btn-ghost !p-2"><ChevronLeft size={15} /></button>
              <button data-testid="calendar-today" onClick={() => { const d = new Date(); setMonth(new Date(d.getFullYear(), d.getMonth(), 1)); }} className="cq-btn cq-btn-ghost !py-1.5">Today</button>
              <button data-testid="calendar-next" onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() + 1, 1))} className="cq-btn cq-btn-ghost !p-2"><ChevronRight size={15} /></button>
            </div>
          </div>
          <div className="hidden md:grid grid-cols-7 gap-px rounded-xl overflow-hidden border border-[#243153] bg-[#243153]" data-testid="calendar-grid">
            {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => <div key={d} className="bg-[#0A1024] px-3 py-2 cq-muted-label">{d}</div>)}
            {days.map((d) => {
              const k = iso(d); const inMonth = d.getMonth() === month.getMonth();
              return (
                <div key={k} className={`bg-[#0A1024] min-h-[110px] p-2 ${inMonth ? "" : "opacity-40"}`}>
                  <div className={`text-xs font-mono mb-1 ${k === today ? "text-[#22E6F2]" : "text-[#7C8BAD]"}`}>{d.getDate()}</div>
                  {items.filter((i) => i.date === k).map((i) => (
                    <button key={i.id} data-testid={`calendar-item-${i.id}`} onClick={() => setEdit(i)} className="w-full text-left mb-1 px-2 py-1 rounded text-[11px] text-[#F7FAFF] truncate hover:brightness-125" style={{ background: `${KIND_COLOR[i.kind] || "#2962FF"}22`, borderLeft: `2px solid ${KIND_COLOR[i.kind] || "#2962FF"}` }}>{i.title}</button>))}
                </div>);
            })}
          </div>
          <div className="space-y-2 mt-6" data-testid="calendar-agenda">
            <div className="cq-muted-label mb-2">Agenda</div>
            {items.map((i) => (
              <div key={i.id} className="cq-card p-4 flex flex-col sm:flex-row sm:items-center gap-3">
                <span className="font-mono text-xs text-[#22E6F2] w-24">{i.date}</span>
                <div className="flex-1 min-w-0"><div className="text-sm text-[#F7FAFF]">{i.title} {i.is_demo && <span className="font-mono text-[9.5px] text-[#F4B860]">DEMO</span>}</div><div className="text-[11px] text-[#7C8BAD]">{i.channel} · {humanize(i.kind)}</div></div>
                <StatusBadge status={i.status} />
                {i.status !== "awaiting_approval" && <button data-testid={`calendar-reschedule-${i.id}`} onClick={() => setResched(i)} className="cq-btn cq-btn-ghost !py-1 !text-xs">Propose reschedule</button>}
                <button data-testid={`calendar-edit-${i.id}`} onClick={() => setEdit(i)} className="cq-btn cq-btn-ghost !py-1 !text-xs">Edit</button>
              </div>))}
          </div>
        </>)}
      </QueryState>
      <RecordDialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)} title={edit?.id ? "Edit calendar item" : "New calendar item"} fields={fields} initial={edit}
        onSubmit={(b) => (edit?.id ? crud.update(edit.id, b) : crud.create(b))} />
      <Reschedule item={resched} onClose={() => setResched(null)} />
    </div>
  );
}
